import React, { memo, useCallback, useRef } from 'react'
import { useParams } from 'react-router'
import { useSortable } from '@dnd-kit/sortable'
import { TtTrayItem, TtTrayVideo } from '@23/react'

import Toolbar from './Toolbar'
import GutterMenu from './GutterMenu'
import useTrayTracking from './useTrayTracking'
import useTrayStore from './useTrayStore'
import { useScrollRegister, useScroll } from './ScrollProvider'
// @ts-expect-error — importing js
import usePlaybackIntent from '../../hooks/usePlaybackIntent'
import { TrayCardVideo } from '../../../firebase/model/tray-cards/types/video-model'
import { setActiveCard, removeCard } from '../../../firebase/hooks/useTray'

interface VideoCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardVideo
}

const VideoCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
}: VideoCardProps): JSX.Element => {
  const { live_id: liveId } = useParams<{ live_id: string }>()
  const { setPlaybackPlayIntent } = usePlaybackIntent(liveId)

  const trayTracking = useTrayTracking()
  const setEditVideoIntention = useTrayStore(
    (state) => state.setEditVideoIntention
  )
  const ref = useRef<HTMLTtTrayItemElement>(null)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const onCardClickHandler = () => {
    trayTracking.activated(card.id)
    setActiveCard(card.id)

    setPlaybackPlayIntent({
      photo_id: card.videoId,
      video_1080p_download: card.playback,
    })
    scrollIntoView(card.id)
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      gutterMode={getGutterMode()}
    >
      <TtTrayVideo
        label={card.label}
        src={card.thumbnail}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        <Toolbar
          cardId={card.id}
          onEditClick={() => {
            trayTracking.startedEdit(card.id)
            setEditVideoIntention(card.id)
          }}
          onDeleteClick={() => {
            trayTracking.deleted(card.id)
            removeCard(card.id)
          }}
          isMediaCard
        />
      </TtTrayVideo>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(VideoCard)
